import { useQuery } from "@tanstack/react-query";
import type { ChangeEvent } from "react";
import { get } from "../../lib/api";
import { type HolderOption, holderOptions } from "../../lib/holders";
import type { HolderListItem } from "../../lib/types";
import { SelectField } from "../ui/Input";

/** One cache entry for every page that lists or picks holders. */
export const HOLDERS_KEY = ["holders"] as const;

export function useHolders() {
  return useQuery({
    queryKey: HOLDERS_KEY,
    queryFn: () => get<HolderListItem[]>("/holders"),
    staleTime: 60_000,
  });
}

/**
 * Picks a holder from the whole tree. `options` narrows the list (a move offers
 * only groups outside the holder's own subtree); by default every holder is
 * offered, since a card may belong to a person or a group alike.
 */
export function HolderSelect({
  label,
  value,
  onChange,
  options,
  hint,
  emptyLabel,
  required,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (holderId: string) => void;
  readonly options?: readonly HolderOption[];
  readonly hint?: string;
  /** Adds a first, empty choice — a filter's "Any", or a move's "Top level". */
  readonly emptyLabel?: string;
  readonly required?: boolean;
}) {
  const holders = useHolders();
  const choices = options ?? holderOptions(holders.data ?? []);
  // A value the list cannot show (not loaded, failed, or just deleted) is still
  // what the form will send, so it is shown rather than silently replaced.
  const unlisted = value !== "" && !choices.some((option) => option.id === value);

  return (
    <SelectField
      label={label}
      value={value}
      {...(required === undefined ? {} : { required })}
      disabled={holders.isPending || holders.isError}
      onChange={(event: ChangeEvent<HTMLSelectElement>) => onChange(event.target.value)}
      {...(hint === undefined ? {} : { hint })}
    >
      {emptyLabel === undefined ? null : <option value="">{emptyLabel}</option>}
      {unlisted ? (
        <option value={value}>
          {holders.isPending
            ? "Loading holders…"
            : holders.isError
              ? "Holders could not be loaded"
              : "Unavailable holder"}
        </option>
      ) : null}
      {choices.map((option) => (
        <option key={option.id} value={option.id}>
          {option.label}
          {option.type === "person" ? " · person" : ""}
        </option>
      ))}
    </SelectField>
  );
}
